import { useCallback, useMemo } from 'react';
import { Box, Card, Stack } from '@mui/material';

import { MetricCardProps, MetricTabs, MetricTabsProps } from '@occc/ui-design-system';
import { RetailerNetworkTobaccoProgramMetricType } from '@occc/graphql';

import {
  LocationRankingTableContainer,
  type LocationRankingMetric,
} from './components/location-ranking-table/location-ranking-table.container';
import { MetricTimeSeriesContainer } from './components/metric-time-series/metric-time-series.container';

type RetailerNetworkTobaccoPerformanceDetailsPageProps = {
  metricItems: MetricTabsProps['items'];
  metricType: RetailerNetworkTobaccoProgramMetricType;
  startDate: Date;
  endDate: Date;
  retailerNetworkUuid: string;
  onMetricTypeChange: (metricKey: RetailerNetworkTobaccoProgramMetricType) => void;
};

const RetailerNetworkTobaccoPerformanceDetailsPage = ({
  metricItems,
  metricType,
  startDate,
  endDate,
  retailerNetworkUuid,
  onMetricTypeChange,
}: RetailerNetworkTobaccoPerformanceDetailsPageProps) => {
  const metricItemIndex = useMemo(
    () => metricItems.findIndex((v) => v.payload === metricType),
    [metricType, metricItems],
  );

  const selectedMetric: LocationRankingMetric | undefined = useMemo(() => {
    const currentItem = metricItems.find((item) => item.payload === metricType);

    if (!currentItem) {
      return undefined;
    }

    return {
      type: currentItem.payload as RetailerNetworkTobaccoProgramMetricType,
      label: currentItem.label,
    };
  }, [metricItems, metricType]);

  const onCurrentMetricChange = useCallback(
    (_index: number, item: MetricCardProps) => {
      onMetricTypeChange(item.payload as RetailerNetworkTobaccoProgramMetricType);
    },
    [onMetricTypeChange],
  );

  return (
    <Stack gap={1}>
      <Card>
        <MetricTabs index={metricItemIndex} items={metricItems} onChange={onCurrentMetricChange}>
          <Box p={2}>
            <MetricTimeSeriesContainer
              metricType={metricType}
              dateFrom={startDate}
              dateTo={endDate}
              retailerNetworkUuid={retailerNetworkUuid}
            />
          </Box>
        </MetricTabs>
      </Card>
      {selectedMetric ? (
        <LocationRankingTableContainer
          key={selectedMetric.type}
          metric={selectedMetric}
          dateFrom={startDate}
          dateTo={endDate}
          retailerNetworkUuid={retailerNetworkUuid}
        />
      ) : null}
    </Stack>
  );
};

export default RetailerNetworkTobaccoPerformanceDetailsPage;
